import { NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, HostListener, OnInit, inject, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';

import { firstValueFrom } from 'rxjs';

import { PermissionCheckService } from '@core/auth/permission-check.service';
import { NavItem, NavLink, NavSection } from '@core/auth/section-access';
import { SessionEndService } from '@core/auth/session-end.service';
import { UserApiService } from '@core/auth/user-api.service';
import { UserService } from '@core/auth/user.service';
import { Apps, WarehouseLocationFormatType } from '@core/enums/enums';
import { NamedResponse } from '@core/models/shared.model';
import { BaseUserModel, UserModel, UserPermissionsModel } from '@core/models/user-model';
import { WarehouseCompResponse } from '@core/models/warehouse-model';
import { ActiveWarehouseService } from '@core/services/active-warehouse.service';
import { AppStorageService } from '@core/services/app-storage.service';
import { BreadcrumbService } from '@core/services/breadcrumb.service';
import { CustomToastService } from '@core/services/custom-toast.service';
import { SetFlagService } from '@core/services/set-flag.service';
import { WarehouseApiService } from '@core/services/warehouse-api.service';
import { WarehouseLocationApiService } from '@core/services/warehouse-location-api.service';
import { environment } from '@env/environment';

import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';

import { buildNavItems, sectionForUrl } from './shell-nav';

type ShellStatus = 'loading' | 'error' | 'noAccess' | 'ready';

interface AppLink {
  id: Apps;
  name: string;
  icon: string;
  url: string;
}

/** The other Brijjworks apps, in the order the switcher shows them. */
const OTHER_APPS: readonly Omit<AppLink, 'url'>[] = [
  { id: Apps.TPMPortal, name: 'TPM', icon: 'assets/brand/app-tpm.svg' },
  { id: Apps.POS, name: 'POS', icon: 'assets/brand/app-pos.svg' },
  { id: Apps.Donation, name: 'Donation', icon: 'assets/brand/app-donation.svg' },
  { id: Apps.Consignment, name: 'Consignment', icon: 'assets/brand/app-consignment.png' },
  { id: Apps.CompanyPortal, name: 'Company Portal', icon: 'assets/brand/brijj-icon.svg' },
  { id: Apps.Inventory, name: 'IMS', icon: 'assets/brand/app-ims.svg' }
];

/** Bootstrap's global bundle (angular.json scripts). */
declare const bootstrap: {
  Modal: {
    getInstance(element: HTMLElement): { dispose(): void } | null;
    new (element: HTMLElement, options: { backdrop: 'static'; keyboard: boolean }): { show(): void };
  };
};

/** Set just before the reload that applies a warehouse switch, so the reloaded page can confirm it once. */
const WAREHOUSE_SWITCHED_KEY = 'lister.ui.warehouseSwitched';

@Component({
  selector: 'app-lister-home',
  templateUrl: './lister-home.component.html',
  styleUrl: './lister-home.component.css',
  imports: [RouterLink, BreadcrumbComponent, RouterLinkActive, RouterOutlet, FormsModule, NgTemplateOutlet]
})
export class ListerHomeComponent implements OnInit {
  private activeWarehouse = inject(ActiveWarehouseService);

  private router = inject(Router);
  private userApiService = inject(UserApiService);
  private userService = inject(UserService);
  private sessionEnd = inject(SessionEndService);
  private breadcrumb = inject(BreadcrumbService);
  private setFlagService = inject(SetFlagService);
  private warehouseLocationService = inject(WarehouseLocationApiService);
  private warehouse = inject(WarehouseApiService);
  private toaster = inject(CustomToastService);
  private storage = inject(AppStorageService);
  private permissionCheck = inject(PermissionCheckService);

  readonly warehouseModal = viewChild.required<ElementRef<HTMLElement>>('warehouseModal');
  readonly mainContent = viewChild.required<ElementRef<HTMLElement>>('mainContent');
  readonly menuButton = viewChild.required<ElementRef<HTMLButtonElement>>('menuButton');
  readonly drawerCloseButton = viewChild.required<ElementRef<HTMLButtonElement>>('drawerCloseButton');

  status: ShellStatus = 'loading';
  navItems: NavItem[] = [];
  activeSection: NavSection | null = null;
  isSubmenuOpen: boolean = false;
  otherApps: AppLink[] = [];
  userRolesPerm: UserPermissionsModel[] = [];
  isDropdownOpen: boolean = false;
  userDetails: BaseUserModel | null = new BaseUserModel();
  user: UserModel = new UserModel();
  hideSideBar: boolean = true;
  /** True when the picker opens because the user has no warehouse yet, rather than to switch. */
  isFirstRun: boolean = false;
  saveAsDefault: boolean = false;
  selectedWarehouseId: number = 0;
  selectedWarehouseLocationId: number | null = null;
  selectedWarehouseName: string = '';
  selectedLocationName: string = '';
  warehouseList: WarehouseCompResponse[] = [];
  warehouseLocationsList: NamedResponse[] = [];
  isLoadingWarehouses: boolean = false;
  isLoadingLocations: boolean = false;
  warehousesLoadFailed: boolean = false;
  locationsLoadFailed: boolean = false;
  isSavingWarehouse: boolean = false;
  showMobileMenu: boolean = false;
  /** True when the permission guard could not load the permissions for the page being opened. */
  permissionCheckFailed: boolean = false;
  /** Numbers each location request, so only the newest one fills the picker. */
  private locationRequest = 0;

  constructor() {
    this.router.events.subscribe(event => {
      if (!(event instanceof NavigationEnd)) return;
      this.permissionCheck.clear();
      if (this.status === 'ready') {
        this.setActiveSidebarByRoute(event.urlAfterRedirects);
      }
    });
    this.permissionCheck.failedUrl$.subscribe(url => (this.permissionCheckFailed = url !== null));
  }

  /** What the main area shows; a failed permission check shows the error state, unless the user has no access at all. */
  get pageState(): ShellStatus {
    return this.permissionCheckFailed && this.status !== 'noAccess' ? 'error' : this.status;
  }

  ngOnInit(): void {
    this.breadcrumb.breadcrumbClick$.subscribe(crumb => {
      if (crumb.label === 'Marketplace') {
        this.isSubmenuOpen = false;
        this.selectSection('marketplaces', false);
      }
    });
    this.setFlagService.sidebarStatus.subscribe(res => {
      this.hideSideBar = res === 'hide';
    });
    this.showSwitchConfirmation();
    void this.loadShell();
  }

  /** Loads the user, their permissions and their warehouse; every failure ends in a state the user can act on. */
  private async loadShell(): Promise<void> {
    this.status = 'loading';
    this.userService.getSessionStorageItem();
    this.userDetails = this.userService.getUserFromLocalStorage();
    // No signed-in user: the session end is already on its way to the login page.
    if (!this.userDetails) return;

    try {
      this.user = await firstValueFrom(this.userApiService.getUserDetails(this.userDetails.userId));
      this.userRolesPerm = await this.userService.getUserRolesandPerm();
    } catch (error) {
      console.error('Could not load the user or their permissions:', error);
      this.status = 'error';
      return;
    }

    this.otherApps = this.buildOtherApps();
    this.navItems = buildNavItems(
      new Set(this.userRolesPerm.map(permission => permission.name)),
      this.breadcrumb.allLinks
    );
    if (this.navItems.length === 0) {
      this.status = 'noAccess';
      return;
    }

    this.status = 'ready';
    this.setActiveSidebarByRoute(this.router.url);
    await this.GetWarehouses();
    this.restoreActiveWarehouse();
    await this.resolveActiveLocationName();
  }

  /** Loads the shell again if it failed, then opens again the page whose permission check failed. */
  async retryShell(): Promise<void> {
    if (this.status === 'error') {
      await this.loadShell();
    }
    const failedUrl = this.permissionCheck.getFailedUrl();
    if (failedUrl !== null && this.status === 'ready') {
      void this.router.navigateByUrl(failedUrl);
    }
  }

  private buildOtherApps(): AppLink[] {
    const granted = new Set((this.user.apps ?? []).map(app => app.id));
    return OTHER_APPS.filter(app => granted.has(app.id)).map(app => ({ ...app, url: this.getAppUrl(app.id) }));
  }

  private getAppUrl(appId: Apps): string {
    switch (appId) {
      case Apps.TPMPortal:
        return `${environment.productionSystemWebBaseUrl.split('?')[0]}?appId=${appId}`;
      case Apps.CompanyPortal:
        return environment.companyWebBaseUrl;
      case Apps.POS:
        return environment.posUrl;
      case Apps.Donation:
        return environment.donationWebBaseUrl;
      case Apps.Consignment:
        return environment.consignmentWebBaseUrl;
      case Apps.Inventory:
        return environment.inventoryWebBaseUrl;
      default:
        return '';
    }
  }

  /**
   * Keeps the warehouse the user last switched to, even when it was not saved as their preference; the user's
   * default applies only when nothing usable is active yet.
   */
  private restoreActiveWarehouse(): void {
    const activeId = this.activeWarehouse.getId();
    const activeWarehouse = activeId ? this.warehouseList.find(x => x.id === activeId) : undefined;
    // When the list failed to load, the stored choice cannot be checked, so it is kept rather than replaced.
    const isKnown = !!activeWarehouse || (!!activeId && this.warehousesLoadFailed);
    if (isKnown && this.activeWarehouse.getLocationId()) {
      this.selectedWarehouseName = this.activeWarehouse.getName() ?? activeWarehouse?.name ?? '';
      return;
    }

    // The default is usable only as a full pair, and only when the warehouse is still one of the user's.
    const selectedWarehouse = this.warehouseList.find(x => x.id === this.user.defaultWarehouseId);
    const isDefaultUsable =
      !!this.user.defaultWarehouseId &&
      !!this.user.listerDefaultWarehouseLocationId &&
      (!!selectedWarehouse || this.warehousesLoadFailed);
    if (isDefaultUsable) {
      this.activeWarehouse.setId(this.user.defaultWarehouseId);
      this.activeWarehouse.setLocationId(this.user.listerDefaultWarehouseLocationId);

      if (selectedWarehouse) {
        this.activeWarehouse.setName(selectedWarehouse.name);
        this.selectedWarehouseName = selectedWarehouse.name;
      } else {
        this.activeWarehouse.setName(null);
      }
    } else {
      // Neither the stored choice nor the default is usable, so the choice is cleared: the picker can then only be
      // left by choosing a warehouse.
      this.activeWarehouse.setId(null);
      this.activeWarehouse.setLocationId(null);
      this.activeWarehouse.setName(null);
      void this.switchModule();
    }
  }

  /** Finds the active location's name for the header; without it the header shows the warehouse alone. */
  private async resolveActiveLocationName(): Promise<void> {
    const warehouseId = this.activeWarehouse.getId();
    const locationId = this.activeWarehouse.getLocationId();
    if (!warehouseId || !locationId) return;

    try {
      const locations = await firstValueFrom(
        this.warehouseLocationService.getWarehouseLocationsLite({
          CompanyId: Number(this.userDetails?.companyId ?? 0),
          WarehouseId: warehouseId
        })
      );
      this.selectedLocationName = locations.find(x => x.id === locationId)?.name ?? '';
    } catch (error) {
      console.error('Error fetching the active location:', error);
    }
  }

  get warehouseLabel(): string {
    if (!this.selectedWarehouseName) return 'Choose a warehouse';
    return this.selectedLocationName
      ? `${this.selectedWarehouseName} - ${this.selectedLocationName}`
      : this.selectedWarehouseName;
  }

  get activeItem(): NavItem | undefined {
    return this.navItems.find(item => item.section === this.activeSection);
  }

  get activeSubmenu(): NavLink[] {
    return this.activeItem?.children ?? [];
  }

  /** Marks a section active; a section with sub-pages opens its submenu, and a second click on it closes it. */
  selectSection(section: NavSection | null, isRouteNavigation: boolean): void {
    const item = this.navItems.find(navItem => navItem.section === section);
    const hasSubmenu = !!item && item.children.length > 1;
    if (!isRouteNavigation && section === this.activeSection && this.isSubmenuOpen) {
      this.isSubmenuOpen = false;
    } else {
      this.isSubmenuOpen = hasSubmenu;
    }
    this.activeSection = section;
  }

  onSubmenuSectionClick(item: NavItem): void {
    const wasActive = this.activeSection === item.section;
    this.selectSection(item.section, false);
    if (!wasActive) {
      void this.router.navigateByUrl(item.route);
    }
  }

  collapseMenu(): void {
    this.isSubmenuOpen = false;
  }

  signOut(): void {
    this.sessionEnd.endByUser();
  }

  private setActiveSidebarByRoute(currentRoute: string): void {
    this.selectSection(sectionForUrl(currentRoute), true);
  }

  skipToMain(event: Event): void {
    event.preventDefault();
    this.mainContent().nativeElement.focus();
  }

  /** Returns nothing on purpose: Angular cancels an event whose listener returns false, which keeps the menu open. */
  setDropdownOpen(isOpen: boolean): void {
    this.isDropdownOpen = isOpen;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.showMobileMenu) {
      this.closeMobileMenu();
    }
  }

  /** Opens the picker at once, in its loading state, and fills it as the choices arrive. */
  async switchModule(): Promise<void> {
    this.isFirstRun = !this.canCloseWarehouseModal();
    // Only the first choice is saved as the user's default unless they ask for it.
    this.saveAsDefault = this.isFirstRun;
    this.isLoadingWarehouses = true;
    this.openWarehouseModal();
    await this.loadWarehouseChoices();
  }

  /** Escape closes the picker only when the user already has a warehouse to go back to. */
  private openWarehouseModal(): void {
    const element = this.warehouseModal().nativeElement;
    // Bootstrap reads the options when the modal is created, so each opening creates it again.
    bootstrap.Modal.getInstance(element)?.dispose();
    new bootstrap.Modal(element, { backdrop: 'static', keyboard: !this.isFirstRun }).show();
  }

  /** Keeps the picker open while a switch is being saved; the switch ends in a reload or an error toast. */
  onWarehouseModalHide(event: Event): void {
    if (this.isSavingWarehouse) {
      event.preventDefault();
    }
  }

  private async loadWarehouseChoices(): Promise<void> {
    this.isLoadingWarehouses = true;
    await this.GetWarehouses();
    this.isLoadingWarehouses = false;
    if (this.warehousesLoadFailed || this.warehouseList.length === 0) return;

    // If user has a default warehouse that is still one of theirs, set it as selected
    if (this.warehouseList.some(x => x.id === this.user.defaultWarehouseId)) {
      this.selectedWarehouseId = this.user.defaultWarehouseId;
    } else if (!this.warehouseList.some(x => x.id === this.selectedWarehouseId)) {
      // fallback: select the first warehouse
      this.selectedWarehouseId = this.warehouseList[0].id;
    }

    await this.getWarehouseLocations();
  }

  retryWarehouseLoad(): void {
    if (this.warehousesLoadFailed) {
      void this.loadWarehouseChoices();
    } else {
      void this.getWarehouseLocations();
    }
  }

  /** The picker can be dismissed only when the user already has a warehouse to go back to. */
  canCloseWarehouseModal(): boolean {
    return this.activeWarehouse.getId() !== null && this.activeWarehouse.getLocationId() !== null;
  }

  /** A user who cannot close the picker, or has no warehouse to choose, can always sign out instead. */
  get showModalSignOut(): boolean {
    const hasNoWarehouses = !this.isLoadingWarehouses && !this.warehousesLoadFailed && this.warehouseList.length === 0;
    return !this.canCloseWarehouseModal() || hasNoWarehouses;
  }

  /** Why Continue is disabled, shown next to it; empty when the choice can be saved. */
  get continueBlockedReason(): string {
    if (this.isLoadingWarehouses) return 'Loading warehouses...';
    if (this.warehousesLoadFailed) return 'Warehouses could not be loaded.';
    if (!this.selectedWarehouseId) return 'Choose a warehouse to continue.';
    if (this.isLoadingLocations) return 'Loading locations...';
    if (this.locationsLoadFailed) return 'Locations could not be loaded.';
    if (this.warehouseLocationsList.length === 0)
      return 'This warehouse has no standard locations. Choose another one.';
    if (!this.selectedWarehouseLocationId) return 'Choose a location to continue.';
    return '';
  }

  /**
   * The modal stays open until the switch succeeds; every open page reads the warehouse when it loads, so a
   * successful switch reloads the page.
   */
  SaveWarehouse(): void {
    this.isSavingWarehouse = true;
    if (!this.saveAsDefault) {
      this.finishWarehouseSwitch();
      return;
    }

    // Saving as the user's default: the active warehouse changes only once the API has accepted it.
    this.userApiService
      .setDefaultWarehouse(this.userDetails!.userId, this.selectedWarehouseId, this.selectedWarehouseLocationId, true)
      .subscribe({
        next: () => this.finishWarehouseSwitch(),
        error: err => {
          console.error('Error setting default warehouse:', err);
          this.isSavingWarehouse = false;
          this.toaster.error('Could not switch the warehouse. Try again.');
        }
      });
  }

  private finishWarehouseSwitch(): void {
    const warehouseName = this.warehouseList.find(x => x.id === this.selectedWarehouseId)?.name ?? '';
    const locationName = this.warehouseLocationsList.find(x => x.id === this.selectedWarehouseLocationId)?.name ?? '';
    this.applyWarehouseSelection();
    this.storage.set(
      'session',
      WAREHOUSE_SWITCHED_KEY,
      [warehouseName, locationName].filter(name => name !== '').join(' - ')
    );
    window.location.reload();
  }

  /** Confirms, once, the warehouse switch the previous page load applied. */
  private showSwitchConfirmation(): void {
    const switchedTo = this.storage.get('session', WAREHOUSE_SWITCHED_KEY);
    if (switchedTo === null) return;
    this.storage.remove('session', WAREHOUSE_SWITCHED_KEY);
    this.toaster.success(switchedTo ? `Warehouse switched to ${switchedTo}.` : 'Warehouse switched.');
  }

  private applyWarehouseSelection(): void {
    this.activeWarehouse.setLocationId(this.selectedWarehouseLocationId);
    this.activeWarehouse.setName(this.warehouseList.find(x => x.id === this.selectedWarehouseId)?.name ?? null);
    this.warehouse.setWarehouse(this.selectedWarehouseId);
  }

  async GetWarehouses(): Promise<void> {
    try {
      this.warehouseList = await firstValueFrom(this.warehouse.getWarehouses({ CompanyId: this.user.companyId }));
      this.warehousesLoadFailed = false;
    } catch (error) {
      console.error('Error fetching warehouses:', error);
      this.warehousesLoadFailed = true;
    }
  }

  async getWarehouseLocations(): Promise<void> {
    const request = ++this.locationRequest;
    const warehouseId = this.selectedWarehouseId;
    this.warehouseLocationsList = [];
    this.selectedWarehouseLocationId = null;
    this.locationsLoadFailed = false;
    this.isLoadingLocations = true;

    try {
      const res = await firstValueFrom(
        this.warehouseLocationService.getWarehouseLocationsLite({
          CompanyId: Number(this.userDetails?.companyId ?? 0),
          WarehouseId: warehouseId,
          WarehouseLocationFormatType: WarehouseLocationFormatType.Standard
        })
      );
      // The user picked another warehouse, or retried, while this request was loading.
      if (request !== this.locationRequest) return;
      this.warehouseLocationsList = res;

      // Preselect the user's default location when it belongs to this warehouse.
      if (
        this.user.listerDefaultWarehouseLocationId &&
        this.warehouseLocationsList.some(x => x.id === this.user.listerDefaultWarehouseLocationId)
      ) {
        this.selectedWarehouseLocationId = this.user.listerDefaultWarehouseLocationId;
      }
    } catch (error) {
      console.error('Error fetching locations', error);
      if (request === this.locationRequest) {
        this.locationsLoadFailed = true;
      }
    } finally {
      // A newer request owns the loading state.
      if (request === this.locationRequest) {
        this.isLoadingLocations = false;
      }
    }
  }

  openMobileMenu(): void {
    this.showMobileMenu = true;
    // The drawer stops being inert on the next render; focus can only move into it after that.
    setTimeout(() => this.drawerCloseButton().nativeElement.focus());
  }

  closeMobileMenu(): void {
    if (!this.showMobileMenu) return;
    this.showMobileMenu = false;
    this.menuButton().nativeElement.focus();
  }

  switchWarehouseMobile(): void {
    this.closeMobileMenu();
    void this.switchModule();
  }
}
